export const Boarding_SOP = {
    boardingIntake: {
        title: "Boarding Intake & Parent Communication",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/boardingIntakeTitle.png",
        body: `
          <section>
            <h3>📝 Setting the Stage for a Safe, Smooth Stay: Boarding Intake & Parent Communication</h3>
            <p>
              At Pawsible, a great boarding experience starts <span class="highlight">before the first night even begins</span>. 
              Clear intake procedures and thoughtful parent communication <span class="highlight-green">set expectations, prevent surprises, and build deep trust</span> with every family we serve.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Boarding Intake</h4>
            <ul>
              <li>🛡️ Ensure the dog's needs, routines, and health concerns are fully understood before arrival</li>
              <li>🐾 Check all essential information provided for safe, personalized, and comfortable care</li>
              <li>📋 Clarify expectations, emergency contacts, and any special services (grooming, training, and medication)</li>
              <li>🤝 Build client confidence through clear, proactive communication</li>
            </ul>
            <p><span class="highlight-blue">A strong intake is the foundation of every safe, joyful boarding experience at Pawsible.</span></p>
          </section>
      
          <section>
            <h4>📋 2. What to Confirm at Intake</h4>
            <ul>
              <li>🐶 Dog’s health status (current vaccinations, recent illnesses, mobility issues, appetite changes)</li>
              <li>🛏️ Boarding comfort items (bed, blanket, toys, preferred chews — must be labeled)</li>
              <li>🥣 Feeding instructions (exact food amounts, timing, any feeding quirks)</li>
              <li>💊 Medication instructions (clear dosage, administration method, side effect notes)</li>
              <li>🚶 Potty schedule preferences (especially for seniors or young dogs)</li>
              <li>🧘 Emotional support needs (separation anxiety, bonding routines, calming activities)</li>
              <li>⚠️ Behavioral flags (dog-selective, noise reactivity, crate anxiety, escape attempts)</li>
              <li>📞 Emergency contact and authorized pickup persons</li>
            </ul>
            <p><span class="highlight-green">Details matter — gathering full context allows us to customize care and prevent stressors during the stay.</span></p>
          </section>
      
            <section>
                <h4>🛡️ 3. Final Verifications at Drop-Off</h4>
                <ul>
                    <li>👀 Visual health/behavior check (no signs of illness, coughing, discharge, limping, or injury)</li>
                    <li>🧺 Inventory of belongings (label and log items)</li>
                    <li>📋 Quick verbal reconfirmation of food, meds, and emergency contacts</li>
                    <li>📲 Double-check that boarding dashboard pet profile is complete and up-to-date</li>
                </ul>
                <p><span class="highlight-red">If any signs of contagious illness (coughing, diarrhea, vomiting, discharge, lethargy) are observed, isolate the dog immediately and notify the manager. Early action protects all guests and staff.</span></p>
            </section>

          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">A little extra preparation upfront creates a lot more peace of mind later.</span> 
              At Pawsible, every boarding stay begins with careful intake, personalized notes, and open communication — giving our guests (and their humans) a truly stress-free experience.
            </p>
          </section>
        `
      },
      mealMedicationLogs: {
        title: "Meal & Medication Logs",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/mealMedicationLogsTitle.png",
        body: `
          <section>
            <h3>🍽️ Health and Happiness: Meal & Medication Logs</h3>
            <p>
              At Pawsible, <span class="highlight">executing accurate feeding and medication tasks</span> is critical for our guests' wellbeing. 
              <strong>Meal & Medication Logs</strong> ensure <span class="highlight-green">every dog’s care instructions — already provided by owners — are precisely followed, recorded, and completed in real-time</span>.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Logging Meals and Medications</h4>
            <ul>
              <li>🍽️ Ensure every dog's established feeding and medication routine is honored without deviations</li>
              <li>🧠 Support clear, real-time updates between shifts and management</li>
              <li>📋 Document consistency for client confidence and operational accountability</li>
              <li>🛡️ Minimize risks by providing immediate alerts if issues arise</li>
            </ul>
            <p><span class="highlight-blue">Accurate, real-time logging protects every dog's health — and keeps the entire team aligned.</span></p>
          </section>
      
          <section>
            <h4>📋 2. How to Complete Meal & Medication Tasks</h4>
            <ul>
              <li>📲 Follow all feeding and medication instructions listed on the Pawsible app boarding task board — do not guess or modify without manager approval</li>
              <li>🕒 Administer feedings and medications as close to the scheduled time as possible (within 30 minutes if possible)</li>
              <li>✅ Immediately after completion, mark the assigned task as “Completed” in the app dashboard</li>
              <li>🧠 If a meal or medication is refused, partially eaten, or issues arise, log details and alert manager immediately</li>
            </ul>
            <p><span class="highlight-green">No manual information gathering is needed — trust and follow the boarding dashboard instructions provided by reception and clients.</span></p>
          </section>
      
          <section>
            <h4>🔍 3. What to Watch For During Feedings and Medication</h4>
            <ul>
              <li>🥣 Appetite changes: refusal, unusually slow eating, nausea signs</li>
              <li>💊 Difficulty administering medication (spitting out, hiding pills, stress behaviors)</li>
              <li>🚨 Unusual reactions post-medication (vomiting, excessive drooling, lethargy)</li>
            </ul>
            <p><span class="highlight-blue">Log observations factually — and escalate health concerns immediately to management.</span></p>
          </section>
      
          <section>
            <h4>⚠️ 4. Critical Attention for Time-Sensitive Medications</h4>
            <ul>
              <li>🚨 Dogs on insulin, seizure control, or heart medications must have doses administered and logged with exact timing</li>
              <li>⏰ These medications cannot be delayed without manager guidance — health risk is too high</li>
              <li>🌟 If a time-sensitive dose is missed for any reason, notify manager immediately</li>
            </ul>
            <p><span class="highlight-red">Precision and urgency are essential when managing time-sensitive medications — lives depend on it.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Executing feeding and medication tasks properly is at the heart of safe, professional boarding care.</span> 
              At Pawsible, we honor each dog's established needs with precision, compassion, and full accountability — every shift, every time.
            </p>
          </section>
        `
      },      
      crateComfortChecks: {
        title: "Crate Comfort Checks & Setup",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/crateComfortChecksTitle.png",
        body: `
          <section>
            <h3>🛏️ Safe, Calm, and Cozy: Crate Comfort Checks & Setup</h3>
            <p>
              At Pawsible, a crate isn't just a space — it's a <span class="highlight">safe haven</span>. 
              Proper setup and daily comfort checks <span class="highlight-green">support emotional wellbeing, healthy rest, and safety during boarding stays</span>.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Crate Comfort Checks</h4>
            <ul>
              <li>🛡️ Provide every dog with a secure, comfortable, and emotionally supportive personal space</li>
              <li>🐾 Reduce stress, overstimulation, and risk of behavioral issues during the stays</li>
              <li>📋 Catch and resolve any bedding issues, crate stress, or hygiene problems early</li>
              <li>🧠 Maintain a consistent, calming environment across all shifts</li>
            </ul>
            <p><span class="highlight-blue">Crates are not about confinement — they’re about comfort, security, and emotional regulation.</span></p>
          </section>
      
          <section>
            <h4>🛏️ 2. Crate Setup Guidelines</h4>
            <ul>
              <li>🧺 Set up crates according to boarding task board instructions for each dog (owner-supplied bedding, toys if approved)</li>
              <li>🛏️ If no bedding is provided, use clean daycare-approved bedding</li>
              <li>🚰 Provide water bowl to crate securely</li>
              <li>🧸 <span class="highlight-red">Do not leave small, chewable, or choke-risk items</span> in the crate</li>
              <li>👀 Visually inspect crate doors and latches for safety and secure locking</li>
            </ul>
            <p><span class="highlight-green">Each crate should feel like a private, peaceful den — personalized whenever possible to match each dog’s comfort needs.</span></p>
          </section>
      
          <section>
            <h4>👀 3. Daily Crate Comfort Check Procedures</h4>
            <ul>
              <li>📲 Follow crate comfort check assignments on the boarding task board</li>
              <li>🧹 Check bedding for wetness, heavy fur buildup, odors, or signs of chewing</li>
              <li>🚿 Replace soiled bedding immediately using clean supplies</li>
              <li>🚰 Refresh mounted water bowls during morning and night shifts</li>
              <li>👀 Monitor for signs of crate stress: excessive scratching, chewing, drooling, or barking</li>
            </ul>
            <p><span class="highlight-blue">Fresh bedding, clean water, and quick intervention keep dogs safe, clean, and emotionally stable during their stay.</span></p>
          </section>
      
          <section>
            <h4>📋 4. How to Log Crate Comfort Checks</h4>
            <ul>
              <li>✅ After completing the check, update the status in the boarding task board</li>
              <li>🚨 If crate stress signs or damage are observed, note immediately and alert manager</li>
              <li>📸 (Optional for documentation) Snap photos of damaged crates, chewed bedding, or concerning behavior if instructed by manager</li>
            </ul>
            <p><span class="highlight-red">Post any noticeable stress signs, crate damage, or concerns in the team chat under "Boarding"</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Comfort is care.</span> 
              At Pawsible, our commitment to crate setup and comfort checks shows our deep respect for every dog's emotional and physical needs — creating a safer, happier boarding environment every day.
            </p>
          </section>
        `
      },      
      boardingDashboardUpdates: {
        title: "Boarding Dashboard Check-Ins & Updates",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/boardingDashboardUpdatesTitle.png",
        body: `
          <section>
            <h3>📲 Real-Time Care: Boarding Dashboard Check-Ins & Updates</h3>
            <p>
              At Pawsible, <span class="highlight">our boarding dashboard is mission control</span> for daily care. 
              Real-time updates ensure <span class="highlight-green">smooth team communication, accurate service execution, and seamless client transparency</span>.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Boarding Dashboard Updates</h4>
            <ul>
              <li>✅ Track completed feedings, medications, potty breaks, crate checks, and wellness observations</li>
              <li>📋 Provide real-time status visibility for managers, shift leads, and next-shift teammates</li>
              <li>🧠 Support quick, informed decision-making if a dog’s needs change during their stay</li>
              <li>🐾 Build a clear care log that strengthens client trust and operational accountability</li>
            </ul>
            <p><span class="highlight-blue">If it’s not logged, it’s not done — real-time updates are part of premium care delivery at Pawsible.</span></p>
          </section>
      
          <section>
            <h4>📝 2. What Must Be Logged in the Boarding Dashboard</h4>
            <ul>
              <li>🍽️ Meals: completion status (full, partial, refused)</li>
              <li>💊 Medications: time given, confirmation of administration</li>
              <li>🚶‍♂️ Potty breaks and walks: completed and any abnormalities noted</li>
              <li>🛏️ Crate comfort checks: completed with any issues logged</li>
              <li>🚨 Health or behavior concerns: documented immediately with timestamp</li>
              <li>📸 Photo/video documentation if instructed by manager for health, incident, or enrichment updates</li>
            </ul>
            <p><span class="highlight-green">Every boarding task has a matching log — keep it clean, consistent, and timely.</span></p>
          </section>
      
          <section>
            <h4>📲 3. How to Log Updates Properly</h4>
            <ul>
              <li>✅ Complete the task for the correct dog profile — double-check names before logging</li>
              <li>🕒 Update the boarding task board immediately after completing the action (not at shift end)</li>
              <li>🔍 Include notes if anything unusual or noteworthy occurred (short, factual, clear)</li>
              <li>🚨 For health concerns, log and escalate to manager immediately — do not just log and move on</li>
            </ul>
            <p><span class="highlight-blue">Consistency protects the pets — and protects your shift team from mistakes or oversights.</span></p>
          </section>
      
          <section>
            <h4>⚡ 4. Best Practices for Dashboard Communication</h4>
            <ul>
              <li>📋 Stay objective and professional — avoid opinions or emotional language in notes</li>
              <li>📸 When uploading photo/video updates, keep content safe, appropriate, and directly related to care</li>
              <li>🧑‍🤝‍🧑 Use team chat for anything urgent that cannot wait for shift transition</li>
              <li>📞 Manager should be informed personally for serious incidents after logging</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The dashboard isn't "extra work" — it's the heart of great boarding care.</span> 
              Logging, updating, and communicating in real time ensures every dog gets the care they deserve — with no gaps, no guessing, and full accountability.
            </p>
          </section>
        `
      },      
      boardingCalmingReactiveDogs: {
        title: "Calming Techniques & Handling Reactive Dogs",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/boardingCalmingReactiveDogsTitle.png",
        body: `
          <section>
            <h3>🧘 Calm Energy, Confident Care: Managing Stress & Reactivity</h3>
            <p>
              At Pawsible, we understand that <span class="highlight">boarding can be stressful for some dogs</span>, especially in a new environment. 
              Our <strong>Calming Techniques</strong> and <strong>Reactive Dog Handling</strong> protocols ensure we <span class="highlight-green">support every guest’s emotional needs with compassion, professionalism, and patience</span>.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Recognizing Stress and Reactivity</h4>
            <ul>
              <li>🐾 Signs of stress: panting, pacing, trembling, whining, excessive barking, lip licking, tucked tail</li>
              <li>🛡️ Signs of reactivity: growling, stiff body posture, hard staring, lunging, air snapping</li>
              <li>👀 Early intervention is key — small stress signs should be soothed before they escalate to defensive behavior</li>
            </ul>
            <p><span class="highlight-blue">Every behavior is communication — we listen with patience, not punishment.</span></p>
          </section>
      
          <section>
            <h4>🧘 2. Calming Techniques to Use</h4>
            <ul>
              <li>🌿 Move slowly and quietly around nervous or reactive dogs — no sudden movements or loud voices</li>
              <li>🧑‍🤝‍🧑 Give space: avoid hovering, crowding, or prolonged direct eye contact</li>
              <li>🐾 Offer calm verbal reassurance using soft, neutral tones ("You're okay, buddy")</li>
              <li>🎯 Allow dogs to approach you if they wish — don't force interaction</li>
              <li>🛏️ Provide crate decompression if appropriate (always ensuring airflow and monitoring)</li>
              <li>🌟 Use enrichment tools: licking mats, safe chews, snuffle mats for calm distraction if needed</li>
            </ul>
            <p><span class="highlight-green">Calm energy invites calm behavior — your body language matters just as much as your words.</span></p>
          </section>
      
          <section>
            <h4>🚨 3. Handling Reactive Moments Safely</h4>
            <ul>
              <li>⛔ Never scold or punish reactive behaviors — this can escalate fear or aggression</li>
              <li>🐕‍🦺 Maintain safe leash handling if escorting a stressed dog (avoid tightening or yanking leash)</li>
              <li>🔄 Redirect gently: lure away with treats or happy voice if possible, not by physical correction</li>
              <li>🏠 If needed, return dog calmly to their crate or decompression area for reset</li>
              <li>📋 Log any notable reactive incidents in the boarding dashboard and notify manager for review</li>
            </ul>
            <p><span class="highlight-blue">Your job is to guide emotions back to balance — not control through force.</span></p>
          </section>
      
          <section>
            <h4>📲 4. Reporting Stress and Reactivity</h4>
            <ul>
              <li>📝 Log stress signs or reactivity events factually (what happened, what response was given, and outcome)</li>
              <li>📸 Capture photo/video for review as needed</li>
              <li>📞 Alert manager immediately if reactivity escalates into safety risk or injury (rare, but critical to handle fast)</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Fear and stress are not bad behaviors — they’re calls for help.</span> 
              At Pawsible, we meet them with patience, professionalism, and love, creating a safer, calmer environment for all.
            </p>
          </section>
        `
      },      
      routineWalksPotty: {
        title: "Routine Walks & Potty Schedule",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/routineWalksPottyTitle.png",
        body: `
          <section>
            <h3>🚶‍♂️ Consistency & Cleanliness: Routine Walks & Potty Schedule</h3>
            <p>
              At Pawsible, <span class="highlight">structured potty breaks and short walks</span> are essential to <span class="highlight-green">support dogs' physical needs, reduce stress, and maintain a clean, healthy environment</span> for all boarding guests.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Scheduled Walks and Potty Breaks</h4>
            <ul>
              <li>🚶‍♂️ Promote regular bathroom habits and healthy digestion</li>
              <li>🧠 Provide mental stimulation and a change of environment</li>
              <li>🧹 Prevent indoor accidents and maintain facility cleanliness</li>
              <li>🐾 Support emotional wellbeing by offering calm outdoor time</li>
            </ul>
            <p><span class="highlight-blue">Routine outdoor breaks create comfort, emotional stability, and respect for each dog's natural needs.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Walks & Potty Break Scheduling Guidelines</h4>
            <ul>
              <li>📲 Follow the assigned potty/walk times listed on each dog's boarding task board profile</li>
              <li>🕒 Standard potty/walk schedule:
                <ul>
                  <li>Morning potty walk (shortly after morning wake-up if stayed overnight)</li>
                  <li>Midday potty break (during daycare quiet time)</li>
                  <li>Evening potty walk (after dinner and final evening crating)</li>
                </ul>
              </li>
              <li>🐕 Puppies, seniors, or dogs with special needs may have extra scheduled breaks — always follow individual care notes</li>
            </ul>
            <p><span class="highlight-green">Consistent, structured potty breaks support healthy digestion, emotional balance, and facility cleanliness.</span></p>
          </section>

          <section>
            <h4>🚶‍♂️ 3. Walk & Potty Break Procedure</h4>
            <ul>
              <li>🔓 Remove the dog safely from their crate using calm, positive handling</li>
              <li>🐾 Attach a leash when exiting the crate area</li>
              <li>🌳 Escort the dog to the designated potty zone (outdoor potty yard or daycare potty section)</li>
              <li>👀 Allow dogs 3–5 minutes to potty calmly — avoid rushing or overstimulating</li>
              <li>🧹 Clean up immediately after each dog (double bag waste if necessary)</li>
              <li>✅ After returning the dog to their crate, mark the potty task as "Completed" in the Pawsible boarding dashboard</li>
            </ul>
            <p><span class="highlight-blue">Follow a calm, consistent, professional routine — even potty breaks are part of building dogs' trust and emotional stability during their stay.</span></p>
          </section>
      
          <section>
            <h4>📲 4. How to Log Walks and Potty Breaks</h4>
            <ul>
              <li>✅ After each assigned walk or potty, update the dog's boarding task board status to "Completed"</li>
              <li>🔍 If any concerning behavior is observed (diarrhea, blood, constipation, straining), note details in the tasks board and alert manager immediately</li>
            </ul>
            <p><span class="highlight-red">Real-time logging ensures all staff know each dog's bathroom needs are met and helps track health patterns.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Freedom, routine, and dignity matter — even during potty breaks.</span> 
              At Pawsible, walks and potty time are moments of care, comfort, and emotional support that help every dog feel at home away from home.
            </p>
          </section>
        `
      },
      boardingAddonServices: {
        title: "Addon Services During Boarding Stay",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/boardingAddonServicesTitle.png",
        body: `
          <section>
            <h3>✨ Elevating the Stay: Addon Services for Boarding Guests</h3>
            <p>
              At Pawsible, boarding isn't just about basic care — it's about <span class="highlight">customized experiences that nurture, enrich, and elevate every pet's stay</span>.
              Our <strong>Addon Services</strong> offer <span class="highlight-green">daycare play, grooming refreshes, training sessions, and emotional support activities</span> tailored to each guest’s needs.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Offering Addon Services</h4>
            <ul>
              <li>🐾 Enrich boarding stays with play, learning, and pampering experiences</li>
              <li>🧠 Support emotional balance, socialization, and physical exercise</li>
              <li>🛁 Help guests go home fresh, clean, and happy</li>
              <li>🤝 Strengthen client loyalty by personalizing each dog's boarding plan</li>
            </ul>
            <p><span class="highlight-blue">Pawsible isn’t just a kennel — it’s a full-service pet resort experience, built with love and intention.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Available Addon Services During Boarding</h4>
            <ul>
              <li>🧸 Daycare Play</li>
              <li>🎯 Private or Group Training (as scheduled)</li>
              <li>🛁 Bath or Grooming (as scheduled)</li>
              <li>🐕‍🦺 Professional Walk (check profile)</li>
            </ul>
            <p><span class="highlight-green">Addons are customizable based on pet personality, energy levels, and parent preferences.</span></p>
          </section>
      
          <section>
            <h4>🛠️ 3. How to Execute and Log Addon Services</h4>
            <ul>
              <li>📲 Check assigned addon services for each boarding guest in the Pawsible boarding dashboard</li>
              <li>✅ Complete the addon service during the scheduled window</li>
              <li>📋 Mark the addon task as "Completed" once service is fulfilled</li>
              <li>💬 Log any special notes (dog's engagement, favorite activity, reaction to session) in the guest's boarding profile</li>
              <li>📸 Capture a photo/video to highlight in team chat under "Boarding"</li>
            </ul>
            <p><span class="highlight-blue">Consistency in execution and documentation ensures addons feel premium, thoughtful, and genuinely caring — never forgotten or rushed.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Boarding is more than housing — it's about heart, joy, and personalization.</span> 
              At Pawsible, every addon service helps turn a stay into an experience pets (and parents) remember — and return for.
            </p>
          </section>
        `
      },      
      boardingPhotoSnapshots: {
        title: "Photo Snapshots for Boarding Guests",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/boardingPhotoSnapshotsTitle.png",
        body: `
          <section>
            <h3>📸 Capturing Confidence: Photo Snapshots for Boarding Guests</h3>
            <p>
              At Pawsible, <span class="highlight">photo and video updates during boarding</span> help <span class="highlight-green">strengthen parent trust, showcase premium care, and document joyful moments</span> throughout each guest's stay.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Boarding Photo/Video Snapshots</h4>
            <ul>
              <li>🐾 Reassure parents that their pet is safe, happy, and thriving during an extended stay</li>
              <li>📚 Provide meaningful updates for report cards or direct client communications</li>
              <li>🧠 Capture emotional moments that reduce parent anxiety (especially for first-time boarders)</li>
              <li>📸 Create visual memories for clients that reinforce loyalty and satisfaction</li>
            </ul>
            <p><span class="highlight-blue">A thoughtful snapshot offers peace of mind and turns boarding into a celebrated experience.</span></p>
          </section>
      
          <section>
            <h4>📋 2. When to Take Boarding Snapshots</h4>
            <ul>
              <li>👋 Early Settling In: Within first 24 hours of boarding stay (relaxed crate time, first potty break, or gentle play if appropriate)</li>
              <li>🌟 Mid-Stay Highlights: 2–4 days into longer stays — relaxed group play, enrichment activities, bonding moments with staff</li>
              <li>🛁 During Daycare or after Grooming: Capture Happy tail moments</li>
              <li>🎉 Special Events: Holiday stays, birthdays, or milestone celebrations ("Max’s 5th day at Pawsible!")</li>
              <li>🧘 Calm Moments: Snuggling with staff, sunbathing in the yard, peacefully resting in crate (especially for anxious or senior dogs)</li>
              <li>✅ Final Day Check-Out: Capture snapshot showing a happy, fresh, ready-to-go-home guest</li>
            </ul>
            <p><span class="highlight-green">Capture calm confidence, joyful milestones, and emotional comfort — not just action shots.</span></p>
          </section>
      
          <section>
            <h4>📸 3. Best Practices for Boarding Snapshots</h4>
            <ul>
              <li>🔆 Use natural lighting whenever possible — no flash (avoid startling dogs)</li>
              <li>🧼 Ensure background areas are tidy (no potty messes, cluttered gates, etc.)</li>
              <li>👀 Focus on the dog’s happy body language: loose tails, relaxed posture, soft eyes</li>
              <li>🎯 Limit to 1–2 strong photos/videos per boarding guest per report period — quality over quantity</li>
              <li>🛡️ No unsafe items or unapproved costumes in photos</li>
            </ul>
          </section>
      
          <section>
            <h4>📲 4. Uploading and Sharing Guidelines</h4>
            <ul>
              <li>✅ Upload snapshot(s) into the team chat under "Boarding"</li>
              <li>💬 Include optional short notes or captions to personalize ("Max loved his splash session today!")</li>
              <li>🛡️ Only staff-approved media should be considered for public social sharing</li>
            </ul>
            <p><span class="highlight-blue">Photos are a service, not an afterthought — document with professionalism, pride, and care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Photos let parents stay emotionally connected — even when they're apart.</span> 
              At Pawsible, every snapshot shows not just where their pet is — but how loved, seen, and well-cared-for they truly are.
            </p>
          </section>
        `
      },      
      departureStatusAppUpdate: {
        title: "Departure Status Update",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/departureStatusAppUpdateTitle.png",
        body: `
          <section>
            <h3>🏡 Smooth Send-Off: Departure Status Update</h3>
            <p>
              At Pawsible, <span class="highlight">a pet’s last moments with us matter just as much as their first</span>. 
              Our <strong>Departure Status Updates</strong> ensure <span class="highlight-green">each boarding guest is checked out cleanly, confidently, and with full transparency</span> for both staff and parents.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Departure Status Updates</h4>
            <ul>
              <li>✅ Confirm all boarding services (feeding, meds, enrichment, grooming) were completed properly</li>
              <li>📋 Provide final wellness check notes to front desk before client pickup</li>
              <li>🛡️ Document pet condition, energy level, and any key updates for client transparency</li>
              <li>🤝 Support a smooth, happy handoff — strengthening client loyalty</li>
            </ul>
            <p><span class="highlight-blue">Clear departures show parents that we cared for their pets with detail and professionalism until the very last minute.</span></p>
          </section>
      
          <section>
            <h4>📝 2. Departure Status Checklist</h4>
            <ul>
              <li>🧹 Ensure dog is clean, brushed (especially after long stays), and looks presentable</li>
              <li>🛏️ Gather and confirm all personal belongings (beds, toys, food containers, meds)</li>
              <li>🍽️ Confirm last meal and potty break were completed (log in boarding dashboard)</li>
              <li>🧠 Perform a brief visual health check: eyes clear, gait normal, coat condition good</li>
              <li>🐾 Calm the dog prior to parent arrival if possible (small walk, decompression time)</li>
              <li>📋 Update departure status in the Pawsible app boarding dashboard — note pet condition and any client follow-up needed</li>
            </ul>
            <p><span class="highlight-green">Departure should feel calm, organized, and celebratory — not rushed or chaotic.</span></p>
          </section>
      
          <section>
            <h4>📲 3. How to Log Departure Status in App</h4>
            <ul>
              <li>✅ Mark "Boarding Stay Completed" on guest's task board after full departure prep is finished</li>
              <li>📝 Log a short departure note: condition of pet, any behavioral notes ("Settled well during stay. Enjoyed enrichment. No concerns.")</li>
              <li>📸 Optional: upload a happy departure photo if available (internal or client share if authorized)</li>
              <li>📞 Flag anything unusual (health concern, missing item, late checkout) to manager immediately before client handoff</li>
            </ul>
            <p><span class="highlight-blue">Departure logging protects operations — and provides closure for pet parents with transparency and care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The final impression stays with the pet parent — make it warm, proud, and professional.</span> 
              Every detail matters, right until the moment we say “see you soon!”
            </p>
          </section>
        `
      },      
}